import React from "react";
import { FiDownload } from "react-icons/fi";
import { useClient } from "../../../../context/ClientsProvider";
import { parseCSVToTable, tableDataToCSV } from "../utils/csvParser";
import {
  downloadFullDataset,
  downloadVisibleTable,
} from "../utils/downloadHelpers";

/**
 * Renders a query-result table with download actions.
 *
 * Preferred source: structured `metadata.columns` / `metadata.rows` emitted
 * by the backend (the exact query result — never round-tripped through the
 * LLM). Fallback source: a CSV string parsed from a legacy ```csv fence in
 * assistant content. When metadata is present we render download buttons;
 * when it's absent (legacy content predating universal table_ids) the table
 * renders plainly with no actions.
 */
const CSVTableComponent = ({ csvContent, metadata }) => {
  const { selectedClient } = useClient();
  const clientId = selectedClient?.id;

  const hasStructuredData =
    Array.isArray(metadata?.columns) &&
    metadata.columns.length > 0 &&
    Array.isArray(metadata?.rows);

  const tableData = hasStructuredData
    ? { headers: metadata.columns, rows: metadata.rows }
    : csvContent
      ? parseCSVToTable(csvContent)
      : null;
  if (!tableData) return <div>Unable to parse CSV data</div>;

  // Download helpers operate on CSV text; synthesize it from the structured
  // cells when we didn't come from a CSV fence.
  const effectiveCsv = csvContent || tableDataToCSV(tableData);

  const hasId = Boolean(metadata && metadata.table_id);
  const isTruncated = hasId && metadata.full_size > metadata.sample_size;

  return (
    <div className="table-wrapper">
      {isTruncated && (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            margin: "1rem 0 0 0",
          }}
        >
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--text-secondary)",
              fontWeight: "400",
            }}
          >
            Showing {metadata.sample_size} of {metadata.full_size} rows
          </span>
        </div>
      )}
      <div className="scrollable-table-container">
        <table className="data-table">
          <thead>
            <tr>
              {tableData.headers.map((header, i) => (
                <th key={i}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasId && (
        <div className="download-buttons-wrapper">
          <button
            className="download-csv-button"
            onClick={() =>
              isTruncated
                ? downloadFullDataset(metadata, effectiveCsv, clientId)
                : downloadVisibleTable(effectiveCsv, metadata)
            }
          >
            {isTruncated
              ? `Download Table (${metadata.full_size} rows)`
              : "Download Table"}
            <FiDownload aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CSVTableComponent;
